/* ─────────────────────────────────────────────────────────────
   The previous Create Schedule page — antd Row/Col with a Delivery Type radio
   row, a Publisher select, and the recurrence pattern laid out inline as four
   always-visible forms. The new design drops all three: Schedule Type is a
   small select, Created By is read-only, and recurrence moved behind
   Repeat → Custom. Previous version in git history.
   ───────────────────────────────────────────────────────────── */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SchedulePageLayout from "./SchedulePageLayout";
import RecipientSelect from "./RecipientSelect";
import PhoneRecipientSelect from "./PhoneRecipientSelect";
/* PublisherSelect is gone from this page — Publisher is a plain input now, with
   no user list to pick from. ShareReportModal still uses the component. */
import ConfirmDialog from "../../components/common/ConfirmDialog";
import Select from "../../components/common/Select/Select";
import InputField from "../../components/Input/Inputfeild";
import CustomRecurrenceModal, { defaultRecurrence, describeRecurrence } from "./CustomRecurrenceModal";
/* The same seven days the Custom dialog's weekly panel offers, so "Weekly on" and the
   dialog cannot disagree about what a weekday is called. */
import { WEEKDAYS } from "./RecurrencePatternsForms/options";
import { getBoardsAsync } from "../../redux/boardSlice";
import buildSchedulePayload, { appListOf, userFromStorage } from "./buildSchedulePayload";
import {
  createSchedule,
  fetchReportBoard,
  fetchScheduleById,
  fetchTimeZones,
  updateSchedule,
  actionError,
  SCHEDULER_POST_ENABLED,
} from "./scheduleApi";
import { formFromScheduleRecord, statusOf } from "./scheduleMappers";
import { selectAllApps, selectLicensedApps } from "../../redux/userSlice";
import { reportAppIdsFor } from "../../utils/appAliases";
import { todayISO } from "../../utils/date-time";
import "./CreateSchedule.css";

/**
 * Create / edit a scheduled report. One page, one component — the route says
 * which mode it is in: /scheduled-reports/create, or
 * /scheduled-reports/edit/:scheduleId to load an existing one.
 *
 * Everything is local state over mock data; nothing is persisted yet.
 */

/* Options we cannot deliver stay visible but unselectable, so the page still
   shows the intended range rather than quietly shrinking. Nothing here is
   unavailable any more, but the mechanism is kept for the next thing that is.

   ── FILE TYPES ──
   All five have a generator behind them now: POST /generate-report on the
   reporting backend renders each from the report's own definition.

   `Link` is gone. It meant writing the finished file to a public Google bucket
   and emailing the URL, and a public URL to private customer data is not
   something to hand out — dropped on the scheduler team's advice.

   `JPEG` became `PNG`. PNG is what the endpoint produces and what the app's own
   Download button produces; JPEG would soften small text and thin table rules
   for no gain. */
const FILE_TYPES = [
  { value: "PDF", enabled: true },
  { value: "HTML", enabled: true },
  { value: "Xls", enabled: true },
  { value: "PNG", enabled: true },
  { value: "CSV", enabled: true },
];

/* ── SCHEDULE TYPES ──
   Email only. The scheduler team confirmed on 2026-09-12 that WhatsApp and SMS are not
   implemented — "currently we have only email" — so a schedule created with either would
   be saved, look published, and deliver to nobody.

   The page is still built for all three: Send To switches to name-and-number entry and
   the payload fills PhoneNumbers, so re-enabling them is these two flags. See
   PhoneRecipientSelect.

   Previous values kept commented for that day. */
const SCHEDULE_TYPES = [
  { value: "Email", enabled: true },
  { value: "WhatsApp", enabled: false },
  { value: "SMS", enabled: false },
  // { value: "WhatsApp", enabled: true },   <- until 2026-09-18
  // { value: "SMS", enabled: true },
];

/**
 * The WEEKDAYS code for a "YYYY-MM-DD" date — "thu" for a Thursday.
 *
 * Built from the string rather than `new Date(value).getDay()`: a bare date string is
 * parsed as UTC midnight, so east of UTC that returns the previous day's weekday. Empty
 * for anything unparseable, which is what leaves "Weekly on" falling back to Monday.
 */
const weekdayCodeOf = (isoDate) => {
  const parts = String(isoDate || "").split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return "";

  const local = new Date(year, month - 1, day);
  if (Number.isNaN(local.getTime())) return "";
  return WEEKDAYS[local.getDay()]?.value || "";
};

/** Email is the only channel that delivers to an address; the rest need numbers. */
const isPhoneChannel = (scheduleType) => scheduleType === "WhatsApp" || scheduleType === "SMS";

/* "YYYY-MM-DDTHH:mm" for a 12-hour form time on a date — sortable as a string,
   so it can be compared directly with nowInZone(). */
const scheduleWallClock = (date, { hour, minute, meridiem }) => {
  if (!date) return "";
  let h = Number(hour) % 12;
  if (meridiem === "PM") h += 12;
  return `${date}T${String(h).padStart(2, "0")}:${minute}`;
};

/* The current wall-clock time in an IANA zone ("Asia/Colombo"), same format.
   The schedule fires in the zone picked on the form, not the browser's, so
   "already passed" has to be judged there. An unknown zone falls back to the
   browser's clock rather than blocking the form. */
const nowInZone = (timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
  } catch (e) {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }
};

/* The first 5-minute slot after now in a zone, as the form's { date, time } —
   what a new schedule starts on, so it opens on a time that can be published
   rather than 01:00 AM. Strictly after: at 10:50 it gives 10:55, not 10:50,
   which would already have passed by the time Publish is pressed. Rolls into
   the next day past 11:55 PM. */
const nextSlotInZone = (timeZone) => {
  const [datePart, timePart] = nowInZone(timeZone).split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  const next = new Date(Date.UTC(y, mo - 1, d, h, Math.floor(mi / 5) * 5 + 5));
  const p = (n) => String(n).padStart(2, "0");
  const hour24 = next.getUTCHours();
  return {
    date: `${next.getUTCFullYear()}-${p(next.getUTCMonth() + 1)}-${p(next.getUTCDate())}`,
    time: {
      hour: p(hour24 % 12 || 12),
      minute: p(next.getUTCMinutes()),
      meridiem: hour24 < 12 ? "AM" : "PM",
    },
  };
};

const HOURS = Array.from({ length: 12 }, (_unused, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_unused, i) => String(i * 5).padStart(2, "0"));

/* No hardcoded timezone list: the scheduler owns which zones it accepts, and a
   guess here would be a zone it might reject. GET /v1/scheduler/countries/
   timezone is the only source — its `Region` ("Asia/Colombo") is the value the
   payload carries. Until it answers there is nothing to choose from, which the
   select says out loud. */

const MESSAGE_LIMIT = 500;

const SaveIcon = (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
    <path
      d="M2.5 3.6A1.1 1.1 0 013.6 2.5h7L13.5 5.4v7a1.1 1.1 0 01-1.1 1.1H3.6a1.1 1.1 0 01-1.1-1.1V3.6z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M5 2.5v3.2h5V2.5M5 13.5v-3.7h6v3.7" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

/* Who the report goes out as, as free text — there is no user API to pick from,
   and a hardcoded list would only offer people who may not exist. Defaults to
   whoever is signed in, which is the common case. */
const defaultPublisher = () => userFromStorage().name || "";

const emptyForm = () => ({
  publisher: "",
  applicationId: "",
  reportId: "",
  date: "",
  time: { hour: "01", minute: "00", meridiem: "AM" },
  timeZone: "", // filled from the timezone API once it loads
  /* "none" | "daily" | "weekly" | "weekdays" | "custom".
     The four simple ones each fill their own block in the payload; only "custom" opens
     the dialog. See buildRecurrence. */
  repeat: "none",
  recurrence: null, // pattern + time, filled by the Custom dialog
  /* The day behind "Weekly on ...", chosen in the select that appears beside Repeat.
     Only read when repeat is "weekly". */
  weeklyDay: "",
  /* When the schedule stops. On the page rather than in the dialog, so it is
     visible without opening anything. */
  range: { type: "none", endBy: "", after: 10 },
  fileType: "PDF",
  scheduleType: "Email",
  subject: "",
  message: "",
  recipients: [],
});

/* Turning a saved schedule back into form state lives in scheduleMappers.js —
   formFromScheduleRecord — since it is the mirror of buildSchedulePayload and
   the two are easier to keep honest side by side. */

export default function CreateSchedule() {
  const history = useHistory();
  const { scheduleId } = useParams();
  const isEdit = Boolean(scheduleId);

  const dispatch = useDispatch();
  const boardsData = useSelector((state) => state.boards?.boardsData);
  const boardsStatus = useSelector((state) => state.boards?.boardsStatus);
  /* Two lists from the registry, and they are used for different things - see the note
     above selectLicensedApps in redux/userSlice.js.

     `apps` is every application. It resolves the report's own stored ApplicationID into
     the payload's ServiceName, and has to keep working for a report built against an
     application this customer holds no licence for. The doc's examples ("WebNeutriPOS")
     match the registry's `Name`, not its DisplayName.

     `licensedApps` is what the Application dropdown offers: only what this user may
     actually pick. A special user gets everything. */
  const apps = useSelector(selectAllApps);
  const licensedApps = useSelector(selectLicensedApps);
  /* 'loading' | 'success' | 'error', set by getAppsAsync. Without it an empty list
     during the fetch is indistinguishable from an account with no applications, and the
     page said "none assigned to your account" before the request had even finished. */
  const appsState = useSelector((state) => state.user?.appsState);
  const appNameById = useMemo(() => new Map(apps.map((a) => [a.ApplicationID, a.Name])), [apps]);

  /* The record being edited, from POST /v1/scheduler/id. Kept whole as well as
     mapped, because saving has to send back the fields the GET does not return
     — dropping them would blank them on the scheduler. */
  const [existingRecord, setExistingRecord] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(isEdit);

  // The boards list is shared, so only fetch it if nothing has already.
  useEffect(() => {
    if (boardsStatus === "default") dispatch(getBoardsAsync());
  }, [dispatch, boardsStatus]);

  const [form, setForm] = useState(() =>
    // Edit mode replaces this once the record arrives; see the effect below.
    // isEdit ? emptyForm() : { ...emptyForm(), publisher: defaultPublisher() },
    /* A new schedule opens on the next free slot from the very first render —
       the browser's zone until the API's zones arrive, then re-aimed at the
       chosen zone (see the effect further down). */
    isEdit ? emptyForm() : { ...emptyForm(), publisher: defaultPublisher(), ...nextSlotInZone() },
  );
  /* Has the user picked a date or time themselves? Until they have, the start
     follows "next free slot" — including when the zone arrives or changes. */
  const whenTouchedRef = useRef(false);

  /* Re-render every 30s so the live "time has passed" check follows the clock:
     a slot picked at 10:48 for 10:50 is in the past at 10:51 without anyone
     touching the form. */
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setClockTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  /* ── REPORTS FOR THE CHOSEN APPLICATION ──
     Only reports are schedulable: dashboards can hold several resources and the
     scheduler payload describes exactly one API call.

     Narrowed by the Application select, which it did not used to be. A report records
     the application it was built for - CreateDashboard writes
     `configDetails.appList: [applicationId]` using the registry's ApplicationID, the
     same id this select holds - so the two do line up.

     Before this the select gated Report Name without filtering it: you had to choose an
     application before the field unlocked, and then saw every report regardless. The
     gate was real and the filtering was not.

     Declared after `form` because it depends on the selection. */
  const reports = useMemo(() => {
    const allReports = (boardsData || []).filter((b) => b?.configDetails?.boardType === "reports");
    if (!form.applicationId) return [];

    /* Matched against every id that counts as this application, not just the selected
       one. The registry holds two entries for the Neurolage product - licences are on
       one, reports on the other - so filtering by the licensed id alone found nothing.
       See utils/appAliases.js. */
    const appIds = reportAppIdsFor(form.applicationId);
    return allReports.filter((b) => appListOf(b).some((id) => appIds.includes(id)));
  }, [boardsData, form.applicationId]);

  const [touched, setTouched] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Edit mode: the loaded schedule's date/time/zone, as "wallclock|zone".
  const loadedWhenRef = useRef("");
  const [customOpen, setCustomOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  /* Separate from confirmLeave: both throw the form away, but one goes back to the list
     and the other stays here with a blank form, so they cannot share a confirm. */
  const [confirmNew, setConfirmNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /* Which button started the save — "Draft" or "Published". `submitting` alone
     drove Publish's label, so Keep as Draft made the Publish button say
     "Saving...". Both stay disabled while either save runs. */
  const [submittingAs, setSubmittingAs] = useState(null);
  const [timeZones, setTimeZones] = useState([]);
  const [timeZonesState, setTimeZonesState] = useState("loading"); // loading | ready | error

  /* The only source for timezones. With no fallback, a failure here leaves the
     field unfillable and TimeZone is Required — so unlike before, this one does
     get surfaced rather than logged quietly.

     The first zone is selected on arrival so the field is never left empty by
     accident; the user can change it. */
  useEffect(() => {
    let cancelled = false;
    fetchTimeZones()
      .then((zones) => {
        if (cancelled) return;
        setTimeZones(zones);
        setTimeZonesState(zones.length > 0 ? "ready" : "error");
        if (zones.length > 0) {
          setForm((f) => (f.timeZone ? f : { ...f, timeZone: zones[0].value }));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setTimeZonesState("error");
        // eslint-disable-next-line no-console
        console.warn("Could not load scheduler timezones.", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* Keeps a default zone in the field rather than seeding it once.
     The fetch above only fires on mount, so anything that later blanks the form —
     New Schedule, or leaving edit mode for /create, both of which rebuild it from
     emptyForm() — left Time Zone empty with nothing to refill it but a page reload.
     Never overwrites a chosen zone: the guard is that the field is empty. */
  useEffect(() => {
    if (form.timeZone || timeZones.length === 0) return;
    setForm((f) => (f.timeZone ? f : { ...f, timeZone: timeZones[0].value }));
  }, [form.timeZone, timeZones]);

  /* A new schedule starts on the next free 5-minute slot in its zone (today, or
     tomorrow just before midnight) instead of a blank date at 01:00 AM, which
     was usually already past. Waits for the zone, since "now" depends on it.
     Only a fresh form is filled: no date yet and the time still the 01:00 AM
     placeholder — anything the user has picked is left alone. New Schedule
     resets to that state, so it is filled again there. */
  // useEffect(() => {
  //   if (isEdit || !form.timeZone || form.date) return;
  //   const t = form.time;
  //   if (!(t.hour === "01" && t.minute === "00" && t.meridiem === "AM")) return;
  //   const { date, time } = nextSlotInZone(form.timeZone);
  //   setForm((f) => (f.date ? f : { ...f, date, time }));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isEdit, form.timeZone, form.date]);
  /* The form already starts on a slot (useState above); this re-aims it at the
     zone once that is known or changed — 11:40 in Colombo is not 11:40 in
     London. Never once the user has set a date or time themselves. */
  useEffect(() => {
    if (isEdit || !form.timeZone || whenTouchedRef.current) return;
    const { date, time } = nextSlotInZone(form.timeZone);
    setForm((f) => ({ ...f, date, time }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, form.timeZone]);

  // Landing straight on an edit URL, or switching between two of them.
  useEffect(() => {
    setTouched(false);
    setShowErrors(false);

    if (!isEdit) {
      setExistingRecord(null);
      setLoadingSchedule(false);
      // setForm({ ...emptyForm(), publisher: defaultPublisher() });
      whenTouchedRef.current = false;
      setForm({ ...emptyForm(), publisher: defaultPublisher(), ...nextSlotInZone() });
      return undefined;
    }

    let cancelled = false;
    setLoadingSchedule(true);
    fetchScheduleById(scheduleId)
      .then((record) => {
        if (cancelled) return;
        setExistingRecord(record);
        // setForm(formFromScheduleRecord(record, emptyForm()));
        const loaded = formFromScheduleRecord(record, emptyForm());
        // What "when" the schedule had, so editing other fields is not blocked by a past start.
        loadedWhenRef.current = `${scheduleWallClock(loaded.date, loaded.time)}|${loaded.timeZone}`;
        setForm(loaded);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(actionError(error, "Loading this schedule failed."));
      })
      .finally(() => {
        if (!cancelled) setLoadingSchedule(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, scheduleId]);

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setTouched(true);
  };

  // A date or time the user chose — from here on it is theirs, not the default slot.
  const setWhen = (patch) => {
    whenTouchedRef.current = true;
    set(patch);
  };

  const setRange = (patch) => {
    setForm((f) => ({ ...f, range: { ...f.range, ...patch } }));
    setTouched(true);
  };

  /* The application the user chose, from the registry list they are licensed for.
     Report Name is not narrowed by it — boards carry no link to an application. */
  const application = licensedApps.find((a) => a.ApplicationID === form.applicationId);

  /* Everything the scheduler marks Required, not just what the design shows an
     asterisk against. Date, Subject, Message and Time Zone are all Required by
     the API — without these, publishing an incomplete form came back as
     "Error in JSON Validation SchedulerRequest", which means nothing to a user. */
  /* A start already in the past for the chosen time zone — the date picker only
     stops earlier DAYS, so today at a time that has gone by slipped through.
     When editing, only a date/time/zone the user changed is held to this: a
     running schedule's original start is in the past by nature, and editing its
     subject or recipients must not demand a new one. */
  const scheduledAt = scheduleWallClock(form.date, form.time);
  // const whenChanged = !isEdit || `${scheduledAt}|${form.timeZone}` !== loadedWhenRef.current;
  /* The exemption is for schedules that are already PUBLISHED — they have been
     running, so a past start is expected. A draft has never run: publishing it
     is its first publish and is held to the same rule as a new schedule, or a
     draft dated last week could be published straight into the past. */
  const editingPublished = isEdit && statusOf(existingRecord) === "Published";
  const whenChanged = !editingPublished || `${scheduledAt}|${form.timeZone}` !== loadedWhenRef.current;
  const startHasPassed = Boolean(scheduledAt) && whenChanged && scheduledAt <= nowInZone(form.timeZone);

  const errors = {
    applicationId: form.applicationId ? "" : "Application is required.",
    reportId: form.reportId ? "" : "Report is required.",
    date: form.date ? "" : "Schedule date is required.",
    time: startHasPassed ? "That time has already passed in the selected time zone. Pick a later time." : "",
    /* An End by before the first send means the schedule never runs. Same-day is
       fine (it sends once). ISO dates compare correctly as strings. Live, like
       the time check — only while End by is the chosen range. */
    endBy:
      form.range.type === "endBy" && form.range.endBy && form.date && form.range.endBy < form.date
        ? "End date can't be earlier than the schedule date."
        : "",
    timeZone: form.timeZone ? "" : "Time zone is required.",
    subject: form.subject.trim() ? "" : "Subject is required.",
    message: form.message.trim() ? "" : "Message is required.",
    recipients: form.recipients.length
      ? ""
      : isPhoneChannel(form.scheduleType)
        ? "Add at least one phone number."
        : "Add at least one recipient.",
  };
  const isValid = Object.values(errors).every((e) => !e);

  /* Options for the shared Select, which takes and returns { label, value }
     objects rather than raw strings. */
  /* ── BOTH NAMES IN THE LABEL, ONLY ONE IN THE PAYLOAD ──
     The registry carries two names for the same application and they disagree: one
     record is "Web Neurolage-WholeSale" by Name and "WITMEG ERP" by DisplayName.

     Neither alone is enough here. Name is what every other screen shows and what the
     payload sends, so a user needs to see it to match this dropdown against the rest of
     the app - but DisplayName is what they were picking from before, and dropping it
     would make familiar applications look like new ones.

     So the label carries both, Name first. Only Name reaches the payload, through
     ServiceName - see appNameById above.

     The option's value is the registry's real ApplicationID, which is the same id the
     scheduler stores - that is what lets an edited schedule re-select its application. */
  /* 'loading' covers the fetch; undefined covers the moment before it starts, when
     getAppsAsync has not run yet and the list is empty for the same reason. Both mean
     "not yet known", which is not the same as "none". */
  const appsLoading = appsState === "loading" || (!appsState && !apps.length);

  const applicationOptions = licensedApps.map((a) => {
    const name = a.Name || a.DisplayName || "";
    const display = a.DisplayName || "";
    return {
      // no brackets when the two agree, or when there is only one of them
      label: display && display !== name ? `${name} (${display})` : name,
      value: a.ApplicationID,
    };
  });
  const reportOptions = reports.map((r) => ({ label: r.name, value: r.id }));
  const hourOptions = HOURS.map((h) => ({ label: h, value: h }));
  const minuteOptions = MINUTES.map((m) => ({ label: m, value: m }));
  const meridiemOptions = [
    { label: "AM", value: "AM" },
    { label: "PM", value: "PM" },
  ];
  /* ── REPEAT ──
     The four simple patterns send their own block, so a plain daily schedule no longer
     means opening the Custom dialog to build one. "Weekly on" takes its day from the
     select beside it rather than from the schedule date - the day a report goes out is
     not always the day the schedule was created. */
  const repeatOptions = [
    { label: "Does not Repeat", value: "none" },
    { label: "Daily", value: "daily" },
    { label: "Weekly on", value: "weekly" },
    { label: "Every weekday (Monday to Friday)", value: "weekdays" },
    { label: "Custom", value: "custom" },
  ];
  const weekdayOptions = WEEKDAYS.map((d) => ({ label: d.label, value: d.value }));
  // Unavailable choices stay listed but unpickable, so the page still shows the
  // intended range rather than quietly shrinking.
  const fileTypeOptions = FILE_TYPES.map((f) => ({
    label: f.enabled ? f.value : `${f.value} (coming soon)`,
    value: f.value,
    disabled: !f.enabled,
  }));
  /* Plain names, no "(coming soon)" suffix — greyed out is signal enough here, and the
     File Type list above is the one that earns the wording. */
  const scheduleTypeOptions = SCHEDULE_TYPES.map((s) => ({
    label: s.value,
    value: s.value,
    disabled: !s.enabled,
  }));

  const pick = (options, value) => options.find((o) => o.value === value) || null;

  const backToList = () => history.push("/scheduled-reports");

  const handleCancel = () => {
    if (touched) {
      setConfirmLeave(true);
      return;
    }
    backToList();
  };

  /* A blank form, ready to fill.
     The time zone is put back rather than left empty: emptyForm() has none, and the
     effect that seeds it from the API runs only on mount — so a reset without this
     leaves Time Zone blank with no way to refill it but a page reload. */
  // const blankForm = () => ({
  //   ...emptyForm(),
  //   publisher: defaultPublisher(),
  //   timeZone: timeZones[0]?.value || "",
  // });
  // ...and on the next free slot in that zone, like a first visit.
  const blankForm = () => {
    whenTouchedRef.current = false;
    const timeZone = timeZones[0]?.value || "";
    return { ...emptyForm(), publisher: defaultPublisher(), timeZone, ...nextSlotInZone(timeZone) };
  };

  /**
   * "New Schedule", from this page.
   *
   * It used to push /scheduled-reports/create unconditionally. From the edit page that
   * works — the route changes, and the effect above rebuilds the form. From the create
   * page it is the route we are already on, so React Router does nothing, the effect
   * does not re-run, and the button looked broken while a half-filled form sat there.
   *
   * So: navigate when that would change something, reset in place when it would not.
   * Either way a filled-in form asks first, because both throw the work away.
   */
  const handleNewSchedule = () => {
    // if (touched) {
    /* Edit mode always asks: New Schedule leaves the schedule being edited, and
       an unchanged edit form counted as "untouched", so it left with no warning. */
    if (touched || isEdit) {
      setConfirmNew(true);
      return;
    }
    startNewSchedule();
  };

  const startNewSchedule = () => {
    setConfirmNew(false);

    if (isEdit) {
      // leaves edit mode; the route effect clears the form and drops existingRecord
      history.push("/scheduled-reports/create");
      return;
    }

    setForm(blankForm());
    setExistingRecord(null);
    setTouched(false);
    setShowErrors(false);
  };

  const handleRepeatChange = (next) => {
    if (next === "custom") {
      // The dialog decides whether it sticks — Cancel there leaves Repeat alone.
      setCustomOpen(true);
      return;
    }

    /* `recurrence: null` on every simple pattern. It holds the Custom dialog's rule, and
       leaving a stale one behind would put the dialog's time into the payload instead of
       the page's - buildRecurrence reads form.recurrence.time first. */
    const patch = { repeat: next, recurrence: null };

    /* "Weekly on" needs a day before it means anything. The schedule date's weekday is
       the sensible opening guess - it is the day the user was already looking at - and
       they can change it in the select that appears. Falls back to Monday when no date
       has been picked yet, rather than leaving the select blank. */
    if (next === "weekly" && !form.weeklyDay) {
      patch.weeklyDay = weekdayCodeOf(form.date) || "mon";
    }

    set(patch);
  };

  const handleCustomSave = (recurrence) => {
    setCustomOpen(false);
    // The dialog owns the time once a custom rule is set, so the page's own
    // time field follows it rather than the two disagreeing.
    set({ repeat: "custom", recurrence, time: recurrence.time });
  };

  const submit = async (status) => {
    // A draft can be incomplete; publishing cannot.
    // if (status === "Published" && !isValid) {
    //   setShowErrors(true);
    //   return;
    // }
    /* ── DRAFTS STILL NEED THE BACKEND'S REQUIRED FIELDS ──
       A draft was meant to be savable half-filled, so it skipped validation. But
       the scheduler API validates every save the same way, Draft or Published —
       Application, Report, Date, Time Zone, Subject, Message and recipients are
       Required on its side — so an incomplete draft was rejected and the user
       only saw "Create schedule failed."

       Until the backend accepts partial drafts, a draft is checked against those
       same fields here, so the user is shown which ones are missing instead.
       The past-time and End by checks stay Publish-only: the API does not
       enforce them, and a draft is not going to be sent yet.

       When the backend relaxes this, drop DRAFT_REQUIRED (or trim it to what it
       still requires) — nothing else depends on it. */
    const DRAFT_REQUIRED = ["applicationId", "reportId", "date", "timeZone", "subject", "message", "recipients"];
    const blocked = status === "Published" ? !isValid : DRAFT_REQUIRED.some((key) => errors[key]);
    if (blocked) {
      setShowErrors(true);
      return;
    }

    setSubmitting(true);
    setSubmittingAs(status);
    try {
      /* Fetched again rather than taken from the list: /dashboard returns
         boards without their resource documents, and the scheduler's BaseURL /
         APIRoute / APIReqParams are read off the resource. */
      const board = await fetchReportBoard(form.reportId);

      const payload = buildSchedulePayload({
        form,
        board,
        user: userFromStorage(),
        status,
        meta: {
          // The report's own app, not the Application select above it.
          // appListOf, not configDetails.appList - the board stores it at the top level,
          // so this resolved to undefined and ServiceName went out empty every time
          serviceName: appNameById.get(appListOf(board)[0]) || "",
          publisherName: form.publisher || "",
        },
      });

      if (isEdit) {
        /* The original record goes underneath the payload: /v1/scheduler/id
           returns only part of a schedule while edit takes the whole thing, so
           sending the form alone would blank every field the GET left out. */
        const res = await updateSchedule({ ...existingRecord, ...payload, ID: scheduleId });
        // eslint-disable-next-line no-console
      } else {
        const res = await createSchedule(payload);
        // eslint-disable-next-line no-console

        if (!SCHEDULER_POST_ENABLED) {
          // eslint-disable-next-line no-console
          console.warn("Scheduler POST is off — payload built and logged, nothing sent.");
        }
      }
      backToList();
    } catch (error) {
      toast.error(actionError(error, `${isEdit ? "Update" : "Create"} schedule failed.`));
    } finally {
      setSubmitting(false);
      setSubmittingAs(null);
    }
  };

  /* Errors appear only after Publish has been pressed — typing into a form and
     being told off field by field is worse than being told once, at the end.
     `showErrors` is set in submit() and never by an onChange. */
  const hasError = (key) => Boolean(showErrors && errors[key]);
  const fieldError = (key) => (hasError(key) ? <div className="error-message">{errors[key]}</div> : null);

  return (
    /* scrollBody: the form is taller than the viewport, and the scrolling stops
       at the title block — the page name and the app bar stay in view. */
    <SchedulePageLayout title="Scheduled Reports" actionLabel="New Schedule" onAction={handleNewSchedule} scrollBody>
      {/* csh-loading: edit mode reads the schedule by id, and until it arrives every
          field holds emptyForm()'s defaults - an empty form that looks finished loading.
          The class turns each control into a shimmer block while the labels stay put, so
          the page keeps its shape and nobody types into a field about to be overwritten.

          Done in CSS from one class rather than a conditional per field: the controls
          keep their own heights, so each skeleton is the size of the control it stands
          in for. See CreateSchedule.css. */}
      <div className={`csh-card${loadingSchedule ? " csh-loading" : ""}`}>
        <div className="csh-grid">
          {/* ── Main column ───────────────────────────── */}
          <div className="csh-main">
            {/* Vertical is gone. It came from a hardcoded verticals.json whose ids
                ("hospitality", "witmeg-erp") matched nothing in the app registry, so it
                narrowed a list of applications that were themselves made up. Application
                now comes from the registry, filtered to this user's licences. */}
            <div className="csh-field">
              <label className="csh-label">
                Application<span className="csh-req">*</span>
              </label>
              <div className="csh-control">
                <Select
                  options={applicationOptions}
                  value={pick(applicationOptions, form.applicationId)}
                  placeholder={
                    appsLoading
                      ? "Loading applications..."
                      : appsState === "error"
                        ? "Could not load applications"
                        : applicationOptions.length
                          ? "Select Application"
                          : "No applications available"
                  }
                  disabled={appsLoading || !applicationOptions.length}
                  error={hasError("applicationId")}
                  /* Changing the application clears the report under it: the report list
                     is filtered by this, so a report from the previous application would
                     otherwise stay selected while no longer being in the list. */
                  onChange={(o) => set({ applicationId: o.value, reportId: "" })}
                />
                {/* Three different empty states, and they used to read as one. "No
                    applications assigned" was shown while the request was still in
                    flight, which made a slow load look like an account with no access. */}
                {appsLoading || appsState === "error" || applicationOptions.length ? (
                  fieldError("applicationId")
                ) : (
                  <div className="csh-hint">No applications are assigned to your account.</div>
                )}
              </div>
            </div>

            <div className="csh-field">
              <label className="csh-label">
                Report Name<span className="csh-req">*</span>
              </label>
              <div className="csh-control">
                <Select
                  options={reportOptions}
                  value={pick(reportOptions, form.reportId)}
                  placeholder={
                    !application
                      ? "Select an application first"
                      : boardsStatus === "pending"
                        ? "Loading reports..."
                        : reports.length === 0
                          ? // says which application, so an empty list is not a mystery
                            "No reports for this application"
                          : "Select Report"
                  }
                  disabled={!application || boardsStatus === "pending"}
                  error={hasError("reportId")}
                  onChange={(o) => set({ reportId: o.value })}
                />
                {fieldError("reportId")}
              </div>
            </div>

            {/* One date, not a range: the end of a repeating schedule is set in
                the Custom dialog's Range of Recurrence instead. */}
            <div className="csh-field">
              <label className="csh-label">
                Schedule<span className="csh-req">*</span>
              </label>
              <div className="csh-control">
                <InputField
                  type="date"
                  className={`csh-date ${hasError("date") ? "input-error" : ""}`}
                  /* Today onwards. A schedule dated yesterday can never fire, so the
                     browser greys out earlier days and refuses an earlier typed value
                     rather than letting one be saved. */
                  min={todayISO()}
                  value={form.date}
                  // onChange={(e) => set({ date: e.target.value })}
                  onChange={(e) => setWhen({ date: e.target.value })}
                  /* Open the calendar from anywhere in the box, not just its icon.
                     showPicker is missing in older browsers and throws outside a
                     user gesture, hence the guard — the icon still works then. */
                  onClick={(e) => {
                    try {
                      e.currentTarget.showPicker?.();
                    } catch (err) {
                      /* typing / the icon still work */
                    }
                  }}
                />
                {fieldError("date")}
              </div>
            </div>

            <div className="csh-field">
              <label className="csh-label" />
              {/* csh-time-row: top-aligned, so the time's error message can sit
                  under the three time selects without pulling the Time Zone label
                  and select down with it. */}
              <div className="csh-control csh-control-row csh-time-row">
                <div className="csh-time-col">
                  <div className="csh-time">
                    <Select
                      className="csh-sel-sm"
                      options={hourOptions}
                      value={pick(hourOptions, form.time.hour)}
                      error={Boolean(errors.time)}
                      onChange={(o) => setWhen({ time: { ...form.time, hour: o.value } })}
                    />
                    <span className="csh-colon">:</span>
                    <Select
                      className="csh-sel-sm"
                      options={minuteOptions}
                      value={pick(minuteOptions, form.time.minute)}
                      error={Boolean(errors.time)}
                      onChange={(o) => setWhen({ time: { ...form.time, minute: o.value } })}
                    />
                    <Select
                      className="csh-sel-sm"
                      options={meridiemOptions}
                      value={pick(meridiemOptions, form.time.meridiem)}
                      error={Boolean(errors.time)}
                      onChange={(o) => setWhen({ time: { ...form.time, meridiem: o.value } })}
                    />
                  </div>
                  {/* Under the time it is about, not off to the side of the row. */}
                  {/* {fieldError("time")} */}
                  {/* Live, unlike the other fields (which wait for Publish): a time
                    that has already gone by is worth knowing the moment it is
                    picked, and it can turn true just by the clock moving on. */}
                  {errors.time && <div className="error-message">{errors.time}</div>}
                </div>
                <span className="csh-inline-label">Time Zone:</span>
                {/* There is no fallback list, so the placeholder says which of
                    the three states this is rather than looking simply empty. */}
                <Select
                  className="csh-sel-grow"
                  options={timeZones}
                  value={pick(timeZones, form.timeZone)}
                  placeholder={timeZonesState === "loading" ? "Loading time zones..." : "Time zones unavailable"}
                  disabled={timeZones.length === 0}
                  error={hasError("timeZone")}
                  onChange={(o) => set({ timeZone: o.value })}
                />
              </div>
              {fieldError("timeZone")}
            </div>

            <div className="csh-field">
              <label className="csh-label">Repeat</label>
              <div className="csh-control">
                {/* The day select sits on the same line as Repeat, so "Weekly on" and the
                    day it names read as one sentence. It appears only for that option —
                    the row is a plain select for every other pattern. */}
                <div className="csh-repeat-row">
                  <Select
                    className={form.repeat === "weekly" ? "csh-sel-grow" : ""}
                    options={repeatOptions}
                    value={pick(repeatOptions, form.repeat)}
                    onChange={(o) => handleRepeatChange(o.value)}
                  />
                  {form.repeat === "weekly" && (
                    <Select
                      className="csh-sel-md"
                      options={weekdayOptions}
                      value={pick(weekdayOptions, form.weeklyDay)}
                      onChange={(o) => set({ weeklyDay: o.value })}
                    />
                  )}
                </div>

                {form.repeat === "custom" && form.recurrence && (
                  <div className="csh-repeat-summary">
                    <span>{describeRecurrence(form.recurrence)}</span>
                    <button type="button" className="csh-link-btn" onClick={() => setCustomOpen(true)}>
                      Change
                    </button>
                  </div>
                )}
                {form.repeat === "none" && <div className="csh-hint">Sends once, on the date and time above.</div>}
                {form.repeat === "weekdays" && (
                  <div className="csh-hint">Sends every Monday to Friday, at the time above.</div>
                )}
              </div>
            </div>

            {/* Range of Recurrence — where the Custom dialog's own copy used to
                be. End by and After share a line; No end date sits under them. */}
            <div className="csh-field">
              <label className="csh-label">Range of Recurrence</label>
              <div className="csh-control">
                {/* has-error: makes room under the row for the End by message, which
                    hangs below the date box (see .csh-range-date-wrap). */}
                <div className={`csh-range ${errors.endBy ? "has-error" : ""}`}>
                  <label className="csh-radio csh-radio-grow">
                    <input
                      type="radio"
                      name="csh-range"
                      checked={form.range.type === "endBy"}
                      onChange={() => setRange({ type: "endBy" })}
                    />
                    <span className="csh-radio-label">End by</span>
                    {/* ── EACH INPUT IS DISABLED UNLESS ITS OWN RADIO IS SELECTED ──
                        Both used to be editable whatever the radio said, with an
                        onFocus that flipped the radio to whichever field was touched.
                        So "No end date" never stuck: clicking into the date silently
                        changed the choice, and a stale value from an abandoned option
                        stayed on screen looking like part of the schedule.

                        The onFocus handlers are gone with that — a disabled input
                        cannot take focus, so keeping them would describe behaviour
                        that no longer exists. */}
                    <span className="csh-range-date-wrap">
                      <InputField
                        type="date"
                        className={`csh-range-date ${errors.endBy ? "input-error" : ""}`}
                        // min={todayISO()}
                        // Nothing before the first send can be picked; typed values are caught by errors.endBy.
                        min={form.date || todayISO()}
                        disabled={form.range.type !== "endBy"}
                        value={form.range.endBy}
                        onChange={(e) => setRange({ type: "endBy", endBy: e.target.value })}
                        // Same as the Schedule date: the whole box opens the calendar.
                        onClick={(e) => {
                          try {
                            e.currentTarget.showPicker?.();
                          } catch (err) {
                            /* typing / the icon still work */
                          }
                        }}
                      />
                      {/* Live, and directly under the End by date it is about. */}
                      {errors.endBy && <div className="error-message csh-range-error">{errors.endBy}</div>}
                    </span>
                  </label>

                  <label className="csh-radio">
                    <input
                      type="radio"
                      name="csh-range"
                      checked={form.range.type === "after"}
                      onChange={() => setRange({ type: "after" })}
                    />
                    <span className="csh-radio-label">After</span>
                    <InputField
                      type="number"
                      min="1"
                      className="csh-range-num"
                      disabled={form.range.type !== "after"}
                      value={form.range.after}
                      onChange={(e) => setRange({ type: "after", after: e.target.value })}
                    />
                    <span className="csh-radio-label">Message(s)</span>
                  </label>

                  <label className="csh-radio csh-radio-block">
                    <input
                      type="radio"
                      name="csh-range"
                      checked={form.range.type === "none"}
                      onChange={() => setRange({ type: "none" })}
                    />
                    <span className="csh-radio-label">No end date</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="csh-field">
              <label className="csh-label">File Type</label>
              <div className="csh-control csh-control-row">
                <Select
                  className="csh-sel-md"
                  options={fileTypeOptions}
                  value={pick(fileTypeOptions, form.fileType)}
                  onChange={(o) => set({ fileType: o.value })}
                />
                <span className="csh-inline-label">Schedule Type:</span>
                <Select
                  className="csh-sel-md"
                  options={scheduleTypeOptions}
                  value={pick(scheduleTypeOptions, form.scheduleType)}
                  /* Changing the channel clears the recipients. An address carries no
                     number and a number carries no address, so whichever way it goes the
                     existing entries would be dropped by the payload — visibly emptying
                     the field is better than publishing to nobody. Same reasoning as the
                     Application select clearing its report. */
                  onChange={(o) =>
                    set(
                      isPhoneChannel(o.value) === isPhoneChannel(form.scheduleType)
                        ? { scheduleType: o.value }
                        : { scheduleType: o.value, recipients: [] },
                    )
                  }
                />
              </div>
            </div>

            <div className="csh-field">
              <label className="csh-label">
                Subject<span className="csh-req">*</span>
              </label>
              <div className="csh-control">
                <InputField
                  type="text"
                  className={hasError("subject") ? "input-error" : ""}
                  placeholder="Enter subject..."
                  value={form.subject}
                  onChange={(e) => set({ subject: e.target.value })}
                />
                {fieldError("subject")}
              </div>
            </div>

            <div className="csh-field">
              <label className="csh-label">
                Message<span className="csh-req">*</span>
              </label>
              <div className="csh-control">
                {/* Stays a textarea — InputField wraps a single-line input, and
                    the message needs the room plus the character counter. */}
                <textarea
                  className={`csh-textarea ${hasError("message") ? "input-error" : ""}`}
                  rows={6}
                  placeholder="Enter message..."
                  maxLength={MESSAGE_LIMIT}
                  value={form.message}
                  onChange={(e) => set({ message: e.target.value })}
                />
                <div className="csh-counter">
                  {form.message.length}/{MESSAGE_LIMIT}
                </div>
                {fieldError("message")}
              </div>
            </div>
          </div>

          {/* ── Side column ───────────────────────────── */}
          <aside className="csh-side">
            {/* Free text, not a picker: there is no user API yet, and the
                17-07-2023 doc takes ScheduleCreatedBy as a plain string. */}
            <div className="csh-side-label">Publisher:</div>
            <InputField
              type="text"
              placeholder="Publisher name..."
              value={form.publisher}
              onChange={(e) => set({ publisher: e.target.value })}
            />

            {/* The control follows the channel: an address field for Email, name and
                number for WhatsApp and SMS. Two components rather than one with a mode —
                see the note at the top of PhoneRecipientSelect.

                RecipientSelect gets no `users` prop, which drops its dropdown and leaves
                pure email entry. Pass a list again when there is one. */}
            <div className="csh-side-label">Send To:</div>
            {isPhoneChannel(form.scheduleType) ? (
              <PhoneRecipientSelect value={form.recipients} onChange={(r) => set({ recipients: r })} />
            ) : (
              <RecipientSelect value={form.recipients} onChange={(r) => set({ recipients: r })} />
            )}
            {fieldError("recipients")}

            <div className="csh-note">
              <span className="csh-note-icon" aria-hidden="true">
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
                  <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M8 7.2v4M8 4.9v.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>
              {isPhoneChannel(form.scheduleType)
                ? "Add as many recipients as you need — enter a name and number, then press Add."
                : "Add as many recipients as you need — type an address and press Enter."}
            </div>
          </aside>
        </div>

        {/* ── Footer ──────────────────────────────────── */}
        <div className="csh-footer">
          <button type="button" className="csh-btn csh-btn-ghost" onClick={handleCancel} disabled={submitting}>
            Cancel
          </button>
          {/* ── BOTH BUTTONS SAVE; THEY DIFFER ONLY IN Status ──
              Draft sends Status "draft", Publish sends "published". Same wording in both
              modes: what the button does to the schedule is the same whether it is being
              created or edited, and in edit mode both go through updateSchedule.

              Blocked while the record is still loading, the same as Publish. This one was
              not, which meant a draft could be saved from a form still showing
              emptyForm()'s defaults - wiping the subject, message and recipients of a
              real schedule. */}
          <button
            type="button"
            className="csh-btn csh-btn-outline"
            onClick={() => submit("Draft")}
            disabled={submitting || loadingSchedule}
          >
            {/* Keep as Draft */}
            {submittingAs === "Draft" ? "Saving..." : "Keep as Draft"}
          </button>
          <button
            type="button"
            className="csh-btn csh-btn-primary"
            onClick={() => submit("Published")}
            disabled={submitting || loadingSchedule}
          >
            {SaveIcon}
            {/* {submitting ? "Saving..." : loadingSchedule ? "Loading..." : "Publish"} */}
            {submittingAs === "Published" ? "Saving..." : loadingSchedule ? "Loading..." : "Publish"}
          </button>
        </div>
      </div>

      <CustomRecurrenceModal
        open={customOpen}
        value={form.recurrence || { ...defaultRecurrence(), time: form.time }}
        onCancel={() => setCustomOpen(false)}
        onSave={handleCustomSave}
      />

      <ConfirmDialog
        open={confirmLeave}
        danger
        title="Discard this schedule?"
        message="Everything you have filled in will be lost."
        confirmText="Discard"
        cancelText="Keep editing"
        onCancel={() => setConfirmLeave(false)}
        onConfirm={backToList}
      />

      {/* Only ever opened when the form has been touched — an untouched form starts a new
          one straight away, since there is nothing to lose by it. */}
      <ConfirmDialog
        open={confirmNew}
        danger
        title="Start a new schedule?"
        // message="Everything you have filled in will be cleared."
        /* Editing, nothing is "cleared" — the saved schedule is kept; what is lost
           is any change made here that has not been saved. */
        message={
          isEdit
            ? "You will leave this schedule. Any changes you have not saved will be lost."
            : "Everything you have filled in will be cleared."
        }
        confirmText="Start new"
        cancelText="Keep editing"
        onCancel={() => setConfirmNew(false)}
        onConfirm={startNewSchedule}
      />
    </SchedulePageLayout>
  );
}
